/**
 * HH Essentials PDP — Product Information, vertical modules (Session 65).
 *
 * The section renders a no-JS-safe vertical stack (all panels visible).
 * CSS under `@media (scripting: enabled)` closes the collapsible panels and
 * applies the description clamp from FIRST PAINT — this file only:
 *   • wires the module toggles (aria-expanded + .is-open on the panel),
 *     honouring their collapse mode ("mobile" = toggle <750px only,
 *     "always" = toggle everywhere);
 *   • verifies the description clamp against REAL rendered overflow —
 *     .is-unclipped when nothing is cut (hides "Read full description"),
 *     .is-expanded on demand (never truncates words, no character counts);
 *   • hides vendor blocks that render as empty holes (CSS :empty can't).
 *
 * Old browsers without `(scripting: enabled)` support get the legacy path:
 * panels closed and .is-clipped applied here, after load (below-the-fold
 * shift — accepted fallback). No external libraries.
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 750px)';
  var SCRIPTING_MQ = '(scripting: enabled)';
  function isDesktop() {
    return window.matchMedia ? window.matchMedia(DESKTOP_MQ).matches : true;
  }
  // True when the browser understood the CSS `(scripting: enabled)` query —
  // i.e. collapse/clamp are already applied by CSS and we must not double-apply.
  function cssHandlesInitialState() {
    return window.matchMedia ? window.matchMedia(SCRIPTING_MQ).matches : false;
  }

  function initComponent(root) {
    if (root.__hhPdpInit) return;
    root.__hhPdpInit = true;

    var component = root.querySelector('[data-hh-pdp-component]');
    if (!component) return;
    var legacy = !cssHandlesInitialState();

    // ---------- Module toggles ----------
    var toggles = Array.prototype.slice.call(component.querySelectorAll('[data-hh-toggle]'));
    toggles.forEach(function (btn) {
      var id = btn.getAttribute('data-hh-toggle');
      var panel = component.querySelector('[data-hh-panel="' + id + '"]');
      if (!panel) return;
      var mode = btn.getAttribute('data-hh-collapse') || 'always';

      function collapsibleNow() {
        return mode === 'always' || !isDesktop();
      }
      function apply() {
        if (collapsibleNow()) {
          var open = panel.classList.contains('is-open');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (legacy) panel.hidden = !open;
        } else {
          // Static heading: panel shown by CSS regardless of .is-open.
          btn.removeAttribute('aria-expanded');
          if (legacy) panel.hidden = false;
        }
      }
      btn.addEventListener('click', function () {
        if (!collapsibleNow()) return;
        var open = !panel.classList.contains('is-open');
        panel.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (legacy) panel.hidden = !open;
      });
      btn.__hhApply = apply;
      apply();
    });

    function applyAllToggles() {
      toggles.forEach(function (b) { if (b.__hhApply) b.__hhApply(); });
    }

    // ---------- Description clamp (real-overflow verification) ----------
    var clip = component.querySelector('[data-hh-clip]');
    var desc = clip ? clip.querySelector('.hh-note') : null;
    var readmore = component.querySelector('[data-hh-readmore]');
    var readmoreLabel = readmore ? readmore.querySelector('[data-hh-readmore-label]') : null;
    var expanded = false;

    // Hide vendor block elements that render as empty holes (whitespace-,
    // &nbsp;- or <br>-only) which CSS :empty cannot match. Never touches blocks
    // that carry text or media (img/table/list/heading/embed). Stored HTML is
    // untouched — we only set `hidden` on the live nodes.
    function normalizeDesc() {
      if (!desc) return;
      var blocks = desc.querySelectorAll('p, div');
      Array.prototype.forEach.call(blocks, function (el) {
        if (el.querySelector('img, picture, figure, table, ul, ol, li, iframe, video, audio, svg, embed, object')) return;
        var txt = (el.textContent || '').replace(/ /g, '').trim();
        if (txt === '') el.hidden = true;
      });
    }

    function measure() {
      if (!clip || !desc || !readmore || expanded) return;
      if (legacy) clip.classList.add('is-clipped');
      var wrap = readmore.parentElement; // .hh-readmore-wrap (lead line + pill)
      var overflowing = (desc.scrollHeight - clip.clientHeight) > 4;
      if (overflowing) {
        clip.classList.remove('is-unclipped');
        if (legacy && wrap) wrap.hidden = false;
      } else {
        clip.classList.add('is-unclipped');
        clip.classList.remove('is-clipped');
        if (legacy && wrap) wrap.hidden = true;
      }
    }

    function setExpanded(next) {
      expanded = next;
      if (!clip || !readmore) return;
      if (next) {
        clip.classList.add('is-expanded');
        clip.classList.remove('is-clipped');
        readmore.setAttribute('aria-expanded', 'true');
        if (readmoreLabel) readmoreLabel.textContent = 'Collapse note ▴';
      } else {
        clip.classList.remove('is-expanded');
        readmore.setAttribute('aria-expanded', 'false');
        if (readmoreLabel) readmoreLabel.textContent = 'See full note ▾';
        measure();
      }
    }

    if (readmore) {
      readmore.addEventListener('click', function () { setExpanded(!expanded); });
    }

    var rafId = null;
    function scheduleMeasure() {
      if (expanded) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        rafId = requestAnimationFrame(measure);
      });
    }

    // Recalc on resize (debounced), fonts and image loads.
    var rt = null;
    window.addEventListener('resize', function () {
      if (rt) clearTimeout(rt);
      rt = setTimeout(function () { applyAllToggles(); scheduleMeasure(); }, 150);
    });
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(scheduleMeasure);
    }
    if (desc) {
      Array.prototype.forEach.call(desc.querySelectorAll('img'), function (img) {
        if (!img.complete) img.addEventListener('load', scheduleMeasure, { once: true });
      });
    }

    normalizeDesc();
    scheduleMeasure();
  }

  function initAll(scope) {
    var hosts = (scope || document).querySelectorAll('hh-pdp-info');
    Array.prototype.forEach.call(hosts, initComponent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme Editor: re-init on section reload.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
